import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    HostBinding,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ITaskV2 } from '../../task-v2/fi-task-v2';
import { IAccessRightFilterCollection, ICommentModel } from '@fi-shared/models';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { filter, Subject, takeUntil } from 'rxjs';
import { RollupContentComponent } from '@fi-shared/rollup/rollup-content.component';
import { AppTextService } from '@fi-shared/app-text';
import { FiTaskV2HelperService } from '../../task-v2/fi-task-v2-helper.service';
import { FiTaskV2Service } from '../../task-v2/fi-task-v2.service';
import { FiTaskStatusType } from '@fi-app/task-v2/fi-task-v2-enums';
import { Animations } from '@fi-shared/utils/animations';
import { SideListOptionType } from '@fi-shared/types';
import { TaskActionsBuilderService } from '../fi-task-v2-report/fi-task-v2-report-widget-details/widget-details-content/widget-details-table/task-actions.builder.service';
import { FiTaskHelperService } from '@fi-shared/fi-task-shared/fi-task-helper.service';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';

@Component({
    selector: 'fi-task-v2-details',
    templateUrl: './fi-task-v2-details.component.html',
    styleUrl: './fi-task-v2-details.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-task-v2-details' },
    animations: [Animations.fadeHeight()],
    standalone: false,
})
export class FiTaskV2DetailsComponent implements OnInit {
    @Input({ required: true }) task: ITaskV2;
    @Input({ required: true }) comments: ICommentModel[];
    @Input({ required: true }) availableAccessRightFilterCollection: IAccessRightFilterCollection;
    @Input() commentId: number;

    @ViewChild(RollupContentComponent, { static: true }) commentsRollupContent: RollupContentComponent;

    @HostBinding('class.automated-task-viewer') get isAutomatedTask(): boolean {
        return this.fiTaskHelperService.isAutomatedTask(this.task.TaskType);
    }

    protected taskMenuItems: IItemsListAction2[] = [];
    private _updateTaskLog$ = new Subject<void>();
    protected get updateTaskLog$() {
        return this._updateTaskLog$.asObservable();
    }

    private archivedTaskMenuItems: IItemsListAction2[] = [
        {
            title: this.appText.get('common.restore'),
            iconClass: 'far fa-arrow-rotate-left',
            condition: () => this.taskActionsBuilderService.canRestoreDeleteTask(this.task),
            onClick: () => this.restoreTask(),
        },
        {
            title: this.appText.get('common.delete_permanently'),
            iconClass: 'far fa-trash',
            condition: () => this.taskActionsBuilderService.canRestoreDeleteTask(this.task),
            onClick: () => this.showConfirmDeleteDialog(),
        },
    ];

    private readonly notStartInProcessTaskMenuItems: IItemsListAction2[] = [
        {
            title: this.appText.get('task.edit_task'),
            iconClass: 'far fa-pen-to-square',
            condition: () =>
                [FiTaskStatusType.NotStarted, FiTaskStatusType.InProcess].includes(this.task.Status) &&
                this.isUserTaskCreator() &&
                !this.task.IsArchived,
            onClick: () => this.editTask(),
        },
        {
            title: this.appText.get('task.send_reminder'),
            iconClass: 'far fa-bell-ring',
            onClick: () => this.sendReminder(),
            condition: () => this.taskActionsBuilderService.canSendRemainder(this.task, false),
        },
    ];

    private readonly completedCancelledTaskMenuItems: IItemsListAction2[] = [
        {
            title: this.appText.get('task.edit_task'),
            iconClass: 'far fa-pen-to-square',
            condition: () =>
                [FiTaskStatusType.Completed, FiTaskStatusType.Cancelled].includes(this.task.Status) &&
                this.isUserTaskCreator() &&
                !this.task.IsArchived,
            onClick: () => this.editTask(),
        },
        {
            title: this.appText.get('task.archive_task'),
            iconClass: 'far fa-box-archive',
            condition: () => this.taskActionsBuilderService.canArchiveTask(this.task, false),
            onClick: () => this.showConfirmArchiveDialog(),
        },
    ];

    constructor(
        private fiStateService: FiStateService,
        private appText: AppTextService,
        private taskActionsBuilderService: TaskActionsBuilderService,
        private fiTaskHelperService: FiTaskHelperService,
        private fiTaskV2HelperService: FiTaskV2HelperService,
        private fiTaskV2Service: FiTaskV2Service,
        private cdr: ChangeDetectorRef,
    ) {}

    // todo: scroll to the comment when it is added.

    public ngOnInit(): void {
        this.setTaskMenuItems();
        this.subscribeToTaskStatusChanges();
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected isUserTaskCreator(): boolean {
        return this.fiTaskV2HelperService.isUserTaskCreator(this.task.CreatedByUserId);
    }

    protected canRestoreTask(): boolean {
        return this.taskActionsBuilderService.canRestoreDeleteTask(this.task);
    }

    protected restoreTask(): void {
        this.saveTaskArchived(false);
    }

    private subscribeToTaskStatusChanges(): void {
        this.fiTaskV2HelperService.taskStatusChange$
            .pipe(
                takeUntil(this.ngOnDestroy$),
                filter(({ task }) => task.Id === this.task.Id),
            )
            .subscribe(({ task, newStatus }) => {
                this.updateTaskStatus(task, newStatus);
            });
    }

    private updateTaskStatus(task: ITaskV2, newStatus: FiTaskStatusType): void {
        //task.Status = newStatus;
        this.updateTaskStatusReq(task, newStatus).then((newTasks: ITaskV2[]) => {
            this.task = newTasks[0];
            this._updateTaskLog$.next();
            this.cdr.markForCheck();
        });
    }

    private updateTaskStatusReq(task: ITaskV2, newStatus: FiTaskStatusType): Promise<ITaskV2[]> {
        return this.fiTaskV2Service.updateTaskStatus([task], newStatus);
    }

    private setTaskMenuItems(): void {
        this.taskMenuItems = [
            ...this.notStartInProcessTaskMenuItems,
            ...this.completedCancelledTaskMenuItems,
            ...this.archivedTaskMenuItems,
        ];
    }

    private editTask(): void {
        this.fiStateService.go(FiStateNames.TaskV2.Edit, { id: this.task.Id });
    }

    private sendReminder(): void {
        this.fiTaskV2Service.sendReminder(this.task.Id).then(() => {
            this.fiTaskV2HelperService.showReminderSentSnackbar();
        });
    }

    private showConfirmArchiveDialog(): void {
        this.fiTaskV2HelperService.showArchiveTaskConfirmDialog().onYesButtonClick(() => {
            this.saveTaskArchived(true);
        });
    }

    private showConfirmDeleteDialog(): void {
        this.fiTaskV2HelperService.showDeleteTaskConfirmDialog().onYesButtonClick(() => {
            this.deletePermanently();
        });
    }

    private saveTaskArchived(isArchived: boolean): void {
        this.fiTaskV2Service.saveTaskArchived([this.task], isArchived).then(() => {
            this.task = {
                ...this.task,
                IsArchived: isArchived,
            };
            this.cdr.markForCheck();
            if (!isArchived) {
                this.fiTaskV2HelperService.showTaskRestoredSnackbar(this.task.Name);
            }
            this._updateTaskLog$.next();
        });
    }

    protected onTaskAssigneesChanged() {
        this._updateTaskLog$.next();
    }

    private deletePermanently(): void {
        this.fiTaskV2Service.deleteTasks([this.task.Id], true).then(() => {
            this.fiStateService.go(FiStateNames.TaskV2.Archived, { filterType: SideListOptionType.SharedWithMe });
            this.fiTaskV2HelperService.showTaskDeletedSnackbar(this.task.Name);
        });
    }
}
